import httpClient from '../http/http-client';
import { IEmissionCalculation } from './emission-calculation.model';

export interface IEditEmissionCalculationResponse {
  message: string;
  count: number;
  emissionCalculationIds: string[];
}

export interface IBulkEditEmissionCalculationResponse {
  message: string;
  updatedEmissions: number;
  createdCalculations: number;
  skipped: { emissionId: string; reason: string }[];
}

export interface IFlagClassification {
  isFlag: boolean;
  flagCategory?: string | null;
}

export interface IDeleteEmissionCalculationsResponse {
  message: string;
  // Ids the backend re-selected because deleting the replacement uncovered a superseded
  // family. Empty when nothing was restored (the record went dark, or an alternative was deleted).
  restoredCalculationIds?: string[];
}

/** Resolved by the record page when a new calculation is added over an existing selection. */
export interface ISupersedeDecision {
  supersede: boolean;
  savedMessage: string;
}

export type SupersedeResolver = () => Promise<ISupersedeDecision>;

export interface IUpdateFlagClassificationResponse {
  message: string;
  updatedEmissions: number;
}

export default class EmissionCalculationService {
  http;

  constructor() {
    this.http = httpClient;
  }

  async getAll(ids?: string[]): Promise<{ count: number, rows: IEmissionCalculation[] }> {
    return this.http.get(`emission-calculation${ids ? `?ids=${ids.join(',')}` : ''}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async deleteEmissionCalculations(emissionCalculationIds: string[]): Promise<IDeleteEmissionCalculationsResponse> {
    return this.http.post(`emission-calculation/delete`, emissionCalculationIds)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async createEmissionCalculation(emissionId: string, transaction: Record<string, unknown>, supersede?: boolean): Promise<IEmissionCalculation> {
    return this.http.post(`emission-calculation/createEmissionCalculation`, { emissionId, transaction, supersede })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async editEmissionCalculation(emissionCalculationIds: string[], transaction: Record<string, unknown>): Promise<IEditEmissionCalculationResponse> {
    return this.http.post(`emission-calculation/editEmissionCalculation`, { emissionCalculationIds, transaction })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async bulkEditSelectedEmissionCalculations(ids: string[], calculation: Record<string, unknown>): Promise<IBulkEditEmissionCalculationResponse> {
    return this.http.post(`emission-calculation/bulk-edit/selected`, { ids, calculation })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async bulkEditAllEmissionCalculations(reportPeriodId: string, calculation: Record<string, unknown>, queryString?: string): Promise<IBulkEditEmissionCalculationResponse> {
    const normalizedQuery = queryString?.replace(/^[?&]/, '').replace(/\?/g, '&') ?? '';
    const queryParams = normalizedQuery ? Object.fromEntries(new URLSearchParams(normalizedQuery)) : {};

    return this.http.post(`emission-calculation/bulk-edit/all`, { reportPeriodId, calculation, ...queryParams })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async changeSelectedStatus(emissionCalculationIds: string[]): Promise<void> {
    return this.http.post(`emission-calculation/changeSelectedStatus`, emissionCalculationIds)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async updateFlagClassification(payload: {
    emissionCalculationIds: string[];
    isFlag: boolean;
    flagCategory?: string | null;
  }): Promise<void> {
    return this.http.post(`emission-calculation/update-flag-classification`, payload)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * Reclassifies FLAG for every calculation belonging to the given emissions. FLAG is stored per
   * calculation but edited as an emission-level attribute, so callers pass emission ids.
   */
  async updateSelectedFlagClassification(ids: string[], flag: IFlagClassification): Promise<IUpdateFlagClassificationResponse> {
    return this.http.post(`emission-calculation/flag-classification/selected`, { ids, flag })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * As above, for every emission matching the current list filters rather than an explicit selection.
   * The new values stay nested under `flag`: the filters spread alongside them can themselves carry
   * isFlag/flagCategory, which would otherwise overwrite what we are trying to set.
   */
  async updateAllFlagClassification(reportPeriodId: string, flag: IFlagClassification, queryString?: string): Promise<IUpdateFlagClassificationResponse> {
    const normalizedQuery = queryString?.replace(/^[?&]/, '').replace(/\?/g, '&') ?? '';
    const queryParams = normalizedQuery ? Object.fromEntries(new URLSearchParams(normalizedQuery)) : {};

    return this.http.post(`emission-calculation/flag-classification/all`, { reportPeriodId, flag, ...queryParams })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }
}
